import { Info } from "lucide-react";
import { formatPrice } from "@/utils/formatPrice";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface Step1TypeProps {
  totalPrice: number;
  totalMaxPlayers: number;
  isMultiSelect: boolean;
  allSameTime: boolean;
  hasCreatableActivityTypes: boolean;
  activityMinPrice: number | null;
  onSelectType: (type: "private" | "open_match" | "activity") => void;
  courtCount?: number;
}

interface TypeCardProps {
  icon: string;
  title: string;
  description: string;
  priceLabel: string;
  onClick: () => void;
  disabled?: boolean;
  disabledReason?: string;
}

function TypeCard({
  icon,
  title,
  description,
  priceLabel,
  onClick,
  disabled = false,
  disabledReason,
}: TypeCardProps) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn(
        "w-full text-left rounded-xl border-2 p-4 transition-colors",
        disabled
          ? "border-muted opacity-50 cursor-not-allowed"
          : "border-muted hover:border-primary/50 hover:bg-primary/5 cursor-pointer active:scale-[0.98]"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-base">
            {icon} {title}
          </p>
          {disabled && disabledReason ? (
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <Info className="h-3 w-3 shrink-0" /> {disabledReason}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground mt-0.5">
              {description}
            </p>
          )}
        </div>
        <span className="text-sm font-semibold whitespace-nowrap shrink-0 mt-0.5">
          {priceLabel} &rarr;
        </span>
      </div>
    </button>
  );
}

export default function Step1Type({
  totalPrice,
  totalMaxPlayers,
  isMultiSelect,
  allSameTime: _allSameTime,
  hasCreatableActivityTypes,
  activityMinPrice,
  onSelectType,
  courtCount = 1,
}: Step1TypeProps) {
  const { t } = useTranslation("booking");
  const perPersonPrice = Math.ceil(totalPrice / totalMaxPlayers);
  const _multiCourt = courtCount > 1;

  return (
    <div className="space-y-4 py-4">
      <h2 className="text-lg font-semibold">{t("howToPlay")}</h2>

      <div className="space-y-3">
        {/* Private */}
        <TypeCard
          icon="🔒"
          title={t("privateBooking")}
          description={t("privateDesc")}
          priceLabel={formatPrice(totalPrice)}
          onClick={() => onSelectType("private")}
        />

        {/* Open match — T1/G-001: creates a real activity (one slot, one time).
            Multi-select is honestly gated (was silently mis-handled before:
            N bookings flagged is_open_match with no activity row). */}
        <TypeCard
          icon="🌍"
          title={t("openMatchType")}
          description={t("openMatchTypeDesc")}
          priceLabel={t("fromPrice", { price: formatPrice(perPersonPrice) })}
          onClick={() => onSelectType("open_match")}
          disabled={isMultiSelect}
          disabledReason={t("multiSelectDisabled")}
        />

        {/* Create activity (activities use existing system, öppen match uses v2) */}
        {hasCreatableActivityTypes && (
          <TypeCard
            icon="🎾"
            title={t("createActivity")}
            description={t("activityDesc")}
            priceLabel={
              activityMinPrice != null
                ? t("fromPrice", { price: formatPrice(activityMinPrice) })
                : t("fromPrice", { price: formatPrice(perPersonPrice) })
            }
            onClick={() => onSelectType("activity")}
          />
        )}
      </div>

    </div>
  );
}
